// components
import { JSX } from 'react';
import { AtText } from '@/components/atoms/at-text/at-text';
import { AtHeader } from '@/components/atoms/at-header/at-header';

// types
import { IOrserviceAreasProps } from './or-service-areas.types';
import {
  ETextVariants,
  ETextSize,
} from '@/components/atoms/at-text/at-text.types';
import { EThemes } from '@/lib/contentstack-sdk/types';
import { IStudioStates } from '@/lib/search/types';
import { OrServiceAreasClient } from './or-service-areas.client';

// utils + hooks
import { stateAbbreviationMap } from './constants';

/**
 * Renders a list of counties grouped by state.
 * Filters out inactive counties (`WebActive: false`).
 *
 */

const StateWithCounties = ({
  data,
}: {
  data: IStudioStates[];
}): JSX.Element => {
  return (
    <>
      {data.map((state: IStudioStates) => {
        const counties = state.Counties ?? [];

        return (
          <div key={state.Id}>
            <AtText variant={ETextVariants.LABEL} className="pb-3 lg:pb-5">
              {stateAbbreviationMap[state.Name] || state.Name}
            </AtText>
            <ul className="grid grid-cols-1 gap-x-4 gap-y-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {counties.map((county) => (
                <li key={county.Id}>
                  <AtText
                    type="span"
                    size={ETextSize.LG}
                    className="text-skin-tertiary"
                  >
                    {county.Name}
                  </AtText>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </>
  );
};

/**
 * Displays a list of counties served by the provided design studio.
 * Grouped by state and styled in a dark-themed section.
 *
 */
export const OrServiceAreas = ({
  designStudio,
}: IOrserviceAreasProps): JSX.Element => {
  const { States, CsName } = designStudio[0];

  return (
    <>
      <OrServiceAreasClient />
      {States && States.length > 0 && (
        <section className="container my-12">
          <div
            className="bg-surface-primary rounded-18 px-6 py-15 lg:rounded-30 lg:px-140 lg:py-120"
            data-theme={EThemes.DARK}
          >
            <AtHeader alt element="h2" variant="h2" className="pb-6 lg:pb-10">
              Counties Served
            </AtHeader>
            <AtText className="pb-4 lg:pb-8">
              {`${CsName}`} serves building in the following counties:
            </AtText>
            <div className="flex flex-col gap-6 lg:gap-8">
              <StateWithCounties data={States} />
            </div>
          </div>
        </section>
      )}
    </>
  );
};
